import { SmartSendProvider } from '~/lib/consts/providers';
import { QuoteState, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { GetClaimForOrderLineId, GetRefundForOrderLineId } from '../cache/ExtendedOrderCache';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { KeyedSmartSendGroupCache } from '../cache/SmartSendGroupCache';
import { ISmartSendContext } from '../context/SmartSendContextModels';
import { ApplierOption } from './ApplierOption';
import { SumNumber, ToNumber, onlyUnique } from './ArrayHelper';
import ItemIsNotReady from './ItemIsNotReady';
import ItemIsReady from './ItemIsReady';
import GenerateOptions from './SmartSendBulkActionOptions';
import SmartSendFilterApplier, {
  DefaultSummary,
  SmartSendFilterContext,
  SmartSendSummary
} from './SmartSendFilterApplier';
import { ToInlineCommaList } from './StringHelper';
import { SmartSendItemGroup } from '../../components/smartsend/item/SmartSendGroup';
import { DefaultFilter } from './DefaultFilter';
import { SmartSendSubType } from '../consts/SmartSendSubType';

export const filterMasterItems = (x: SmartSendItemApiModel) => x.Status !== 'Deleted';

export const IsSmartSendByType = (type: string) => type === 'SmartSend';

export const IsSmartSend = (x: SmartSendItemApiModel) => IsSmartSendByType(x.Type);

export const SmartSendHasSource = (x: SmartSendItemApiModel, source: string) =>
  x.Source.Id === source || x.MarketplaceOrderLines.find((m) => m.Source.Id === source);

export const CanMarkAsDispatched = (x: SmartSendItemApiModel): boolean =>
  x.MarketplaceStatus == 'AwaitingDispatch' && HasExternalTrackingNumber(x);

export const HasExternalTrackingNumber = (x: SmartSendItemApiModel): boolean => !!GetExternalTrackingNumber(x);

export const GetExternalTrackingNumber = (x: SmartSendItemApiModel): string => x?.Meta['ExternalTrackingNumber']?.Value;

export const HasClaims = (item: SmartSendItemApiModel) =>
  !!GetClaimForOrderLineId(item.BookingDetails?.OrderLineId) || !!item.Statuses?.ClaimStatus;

export const IsInternational = (item: SmartSendItemApiModel) =>
  item.Recipient?.CountryIsoCode != item?.Sender?.CountryIsoCode;

export const IsSavedForLater = (item: SmartSendItemApiModel) =>
  item.MarketplaceStatus === 'Sold' && item.Status == 'Inactive';

export const IsDomestic = (item: SmartSendItemApiModel) =>
  item.Recipient?.CountryIsoCode === item.Sender?.CountryIsoCode;

export const IsSelected = (item: SmartSendItemApiModel) => item.State?.selected === true;

export const HasRefund = (item: SmartSendItemApiModel) =>
  !!GetRefundForOrderLineId(item.BookingDetails?.OrderLineId) || !!item.Statuses?.RefundStatus;

export const CanGenerateLabel = (x: SmartSendItemApiModel) =>
  SettingsCache.settings.Features.includes('Labels') &&
  x.BookingDetails?.Service?.Provider === SmartSendProvider &&
  x.BookingDetails?.OrderLineId;

export const IsReturnCollection = (item: SmartSendItemApiModel) => item.Tags?.includes('ReturnCollection');

export const ItemShouldAllowCustomerUpdate = (item: SmartSendItemApiModel) =>
  item.Status == 'Active' && item.MarketplaceStatus == 'Sold' && !IsReturnCollection(item);

const deliveryTypes = ['Shop', 'Locker'];
export const isDeliveryToShopService = (item: SmartSendItemApiModel) =>
  item.State?.validation?.QuoteState?.Quote?.Service?.PickUpProviderCode &&
  deliveryTypes.includes(item.State.validation.QuoteState.Quote.Service.DeliveryType);

export const GetQuoteServiceTagsFromItem = (item: SmartSendItemApiModel): string[] =>
  item?.State?.validation?.QuoteState?.Quote?.Service?.Tags || [];

export const GetQuoteServiceTags = (x: QuoteState): string[] => x?.Quote?.Service?.Tags || [];

export const GetItemServiceTags = (x: SmartSendItemApiModel): string[] =>
  GetQuoteServiceTags(x.State?.validation?.QuoteState);

export const GetItemWeight = (x: SmartSendItemApiModel) =>
  Number(
    (x.Consignment?.Parcels?.length > 0
      ? SumNumber(x.Consignment.Parcels.map((p) => p.Dimensions.Weight))
      : ToNumber(x.PackageDimensions.Weight)
    ).toFixed(2)
  );

export const IsAwaitingDispatch = (x: SmartSendItemApiModel) =>
  IsSmartSend(x) &&
  x.Status === 'Active' &&
  x.MarketplaceStatus === 'AwaitingDispatch' &&
  !x.TrackingSummary?.DroppedOff &&
  !x.TrackingSummary?.Collected;

export const ToItemSource = (x: { Source: { Name: string }; Type?: string }) => {
  if (IsSmartSendByType(x.Type)) {
    return x.Source.Name;
  }
  switch (x.Type) {
    case 'BulkUpload':
    case 'AutoSend':
      return 'Legacy Smart Send';
    case 'OrderProcess':
    case 'OrderProcess:V2.0':
    case 'Reorder':
      return 'Order Process';
    default:
      return 'Non-Smart Send';
  }
};

export const ToItemSources = (x: SmartSendItemApiModel) =>
  ToInlineCommaList(x.MarketplaceOrderLines.map((m) => m.Source.Name).filter(onlyUnique));

export default (
  state: ISmartSendContext,
  soldItemsFilter: SmartSendFilterContext,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[]
): ISmartSendContext => {
  // filterMasterItems does not depend on the tab, so scan the cache once here rather than once per
  // tab inside the loop. Filter keys accumulate as the user navigates and are never removed, so
  // this loop can run over several tabs that are not even mounted.
  const masterItems = smartSendItemsCache.filter(filterMasterItems);

  for (const key in state.filters) {
    const res = GenerateSummaryForMasterItems(
      key,
      resetSelection,
      masterItems,
      key === SmartSendSubType ? soldItemsFilter : state.filters[key].filter
    );
    state.filters[key].summary = res.summary;
    state.filters[key].filter = res.filter;
    KeyedSmartSendGroupCache[key] = res.groups;
  }

  return state;
};

/**
 * Takes the raw item cache. Kept as the entry point for single-tab callers; the shared work happens
 * in GenerateSummaryForMasterItems so the default export can hoist the master filter out of its loop.
 */
export const SmartSendSummaryGeneratorActual = (
  key: string,
  resetSelection: boolean,
  smartSendItemsCache: SmartSendItemApiModel[],
  filter?: SmartSendFilterContext
): { summary: SmartSendSummary; filter: SmartSendFilterContext; groups: SmartSendItemGroup[] } =>
  GenerateSummaryForMasterItems(key, resetSelection, smartSendItemsCache.filter(filterMasterItems), filter);

/** `items` must already have been through filterMasterItems. */
const GenerateSummaryForMasterItems = (
  key: string,
  resetSelection: boolean,
  items: SmartSendItemApiModel[],
  filter?: SmartSendFilterContext
): { summary: SmartSendSummary; filter: SmartSendFilterContext; groups: SmartSendItemGroup[] } => {
  const newFilter: SmartSendFilterContext = filter ? { ...filter } : { ...DefaultFilter() };

  const groups = SmartSendFilterApplier(items, newFilter);

  const newSummary: SmartSendSummary = { ...DefaultSummary() };
  newSummary.count = 0;
  newSummary.ready = 0;
  newSummary.notReady = 0;
  newSummary.savedForLater = 0;
  newSummary.selected = [];
  newSummary.groupCount = groups.length;
  newSummary.states = [];

  // One pass for the stats. Each of these used to re-filter the whole list by IsSmartSend before
  // applying its own predicate, so the list was walked eight times over to produce five numbers.
  let sold = 0;
  let ready = 0;
  let savedForLater = 0;
  let notReady = 0;
  let groupable = 0;
  const seenPropertyKeys = new Set<string>();

  items.forEach((i) => {
    if (!IsSmartSend(i)) {
      return;
    }
    if (i.MarketplaceStatus === 'Sold') {
      sold++;
      if (i.Status === 'Active') {
        // Groupable is the number of items that share a PropertyKey with an earlier one.
        if (seenPropertyKeys.has(i.PropertyKey)) {
          groupable++;
        } else {
          seenPropertyKeys.add(i.PropertyKey);
        }
      }
    }
    if (ItemIsReady(i)) {
      ready++;
    }
    if (IsSavedForLater(i)) {
      savedForLater++;
    }
    if (ItemIsNotReady(i)) {
      notReady++;
    }
  });

  newSummary.stats = [
    { key: 'Sold', value: sold },
    { key: 'Ready', value: ready },
    { key: 'SavedForLater', value: savedForLater },
    { key: 'NotReady', value: notReady },
    { key: 'Groupable', value: groupable }
  ];

  // Accumulated into plain arrays and post-processed once after the loops. Rebuilding these with
  // concat (and, for states, re-running the dedupe) on every iteration made them grow quadratically
  // with the item count.
  const bulkOptions: ApplierOption[] = [];
  const states: string[] = [];

  groups.forEach((g) => {
    g.Items.forEach((i) => {
      if (resetSelection) {
        i.State.selected = false;
      } else {
        newSummary.count++;
        if (IsSelected(i)) {
          newSummary.selected.push(i.Id);
          // Duplicates are deliberate: the action bar sizes its badges by counting how many times
          // an option appears, so one entry per selected item is the count. Do not dedupe this.
          bulkOptions.push(...GenerateOptions(i, i.State?.validation));
        }
      }

      if (ItemIsReady(i)) {
        newSummary.ready++;
      }
      if (ItemIsNotReady(i)) {
        newSummary.notReady++;
      }
      if (IsSavedForLater(i)) {
        newSummary.savedForLater++;
      }
      if (IsAwaitingDispatch(i)) {
        newSummary.awaitingDispatch++;
      }
    });

    states.push(...g.States);
  });

  newSummary.bulkOptions = bulkOptions;
  newSummary.states = states.filter(onlyUnique);

  let indexToShowFrom = newFilter.CurrentFilter.Skip;
  if (indexToShowFrom > newSummary.count) {
    // If they are filtering and the paging is greater than that which can be returned, reset.
    newFilter.CurrentFilter.Skip = 0;
    indexToShowFrom = 0;
  }
  const indexToShowTo = indexToShowFrom + newFilter.CurrentFilter.Take;

  // A Set, not an array - this is membership-tested once per item of every visible group below, and
  // on the Sold tab it holds everything paged out of view, which is most of the list.
  const hidden = new Set<string>();

  if (key === SmartSendSubType) {
    // Show and Hide based on item index
    // SmartSend Is Special as its the only one which holds the full list of items and does paging client side
    let index = 0;
    groups.forEach((g) => {
      let hiddenCount = 0;
      g.Items.forEach((i) => {
        const itemIsHidden = index < indexToShowFrom || index >= indexToShowTo;
        if (itemIsHidden) {
          hidden.add(i.Id);
          hiddenCount++;
        }
        index++;
      });
      g.Hidden = g.Items.length === hiddenCount;
    });
  }

  const visibleGroups = groups
    .filter((x) => !x.Hidden)
    .map((x) => {
      return { ...x, Items: x.Items.filter((i) => !hidden.has(i.Id)) };
    });

  return { summary: newSummary, filter: newFilter, groups: visibleGroups };
};
